import React, { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  Home,
  Megaphone,
  Tv,
  MonitorSmartphone,
  Ticket,
  Sparkles,
  Wallet as WalletIcon,
  ShieldCheck,
  Activity,
  GraduationCap,
  Store,
  LogOut,
  Menu,
  X,
  ChevronDown,
  Radio,
  FolderOpen,
  ListVideo,
  CalendarClock,
  BarChart3,
  Building2,
  Radar,
  Zap,
  Inbox,
  Users,
  LayoutDashboard,
} from "lucide-react";
import BrandLogo from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";

/* ---------- Nav model ---------- */
function useNavGroups(role) {
  // Mirrorloops platform: product-first, then account, then operator (role-scoped).
  const groups = [
    {
      key: "main",
      title: null,
      items: [{ to: "/app/dashboard", label: "Overview", icon: Home, end: true }],
    },
    {
      key: "advertise",
      title: "Advertise",
      accent: "#00E5FF",
      icon: Megaphone,
      items: [
        { to: "/app/ads", label: "Campaigns", icon: Megaphone, end: true },
        { to: "/app/campaigns/new", label: "New campaign", icon: Sparkles },
        { to: "/app/rules/marketplace", label: "Rule marketplace", icon: Zap },
        { to: "/network", label: "Network", icon: Radar },
        { to: "/programmable", label: "Programmable", icon: Zap },
        ...(role === "hq_admin"
          ? [{ to: "/app/ads/review", label: "Ad review", icon: ShieldCheck }]
          : []),
      ],
    },
    {
      key: "signage",
      title: "Signage",
      accent: "#D4B878",
      icon: Tv,
      items: [
        { to: "/app/signage?tab=screens", label: "Screens", icon: Tv, matchPrefix: "/app/signage" },
        { to: "/app/signage?tab=media", label: "Media", icon: FolderOpen },
        { to: "/app/signage?tab=playlists", label: "Playlists", icon: ListVideo },
        { to: "/app/signage?tab=schedules", label: "Schedules", icon: CalendarClock },
        { to: "/app/signage?tab=analytics", label: "Analytics", icon: BarChart3 },
        { to: "/app/proof-of-play", label: "Proof of Play", icon: ShieldCheck },
      ],
    },
    {
      key: "cyber",
      title: "Cyber & Coworking",
      accent: "#CCFF00",
      icon: MonitorSmartphone,
      items: [
        { to: "/app/hubs", label: "Hubs", icon: Building2 },
        { to: "/app/bookings", label: "My bookings", icon: Ticket },
        { to: "/app/memberships", label: "Memberships", icon: Sparkles },
      ],
    },
  ];

  const accountItems = [{ to: "/app/wallet", label: "Wallet", icon: WalletIcon }];

  const operatorItems = [];
  if (role === "staff" || role === "manager" || role === "hq_admin") {
    operatorItems.push({ to: "/app/admin", label: "Hub console", icon: Activity });
  }
  if (role === "franchise_owner") {
    operatorItems.push({ to: "/app/franchise", label: "My franchise", icon: Store });
    operatorItems.push({ to: "/app/admin", label: "Hub console", icon: Activity });
  }
  if (role === "hq_admin") {
    operatorItems.push({ to: "/app/hq", label: "HQ dashboard", icon: ShieldCheck });
    operatorItems.push({ to: "/app/leads", label: "Leads inbox", icon: Inbox });
    operatorItems.push({ to: "/app/admin/partners", label: "Partners review", icon: Users });
    operatorItems.push({ to: "/app/admin/commissions", label: "Commissions", icon: Zap });
    operatorItems.push({ to: "/app/admin/marketplace", label: "Marketplace", icon: Store });
    operatorItems.push({ to: "/app/admin/governance", label: "Governance", icon: ShieldCheck });
    operatorItems.push({ to: "/app/franchises", label: "Franchises", icon: Store });
    operatorItems.push({ to: "/app/institutions", label: "Institutions", icon: GraduationCap });
  }
  if (role === "partner") {
    operatorItems.push({ to: "/app/partner", label: "Partner Command", icon: LayoutDashboard });
  }

  groups.push({ key: "account", title: "Account", items: accountItems });
  if (operatorItems.length) {
    groups.push({ key: "operator", title: "Operator", items: operatorItems });
  }
  return groups;
}

/* ---------- Sidebar ---------- */
function SidebarLink({ item, onNavigate }) {
  const loc = useLocation();
  const target = new URL(item.to, window.location.origin);
  const isActive =
    loc.pathname === target.pathname &&
    (!item.matchPrefix || loc.pathname.startsWith(item.matchPrefix)) &&
    (!target.search || loc.search === target.search);
  // Fallback: exact pathname match for items without query param
  const activeClass = isActive
    ? "bg-white/[0.06] text-white border-l-2 border-white"
    : "text-white/50 hover:text-white hover:bg-white/[0.03] border-l-2 border-transparent";
  const I = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={onNavigate}
      data-testid={`sidebar-nav-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      className={() =>
        `flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition ${activeClass}`
      }
    >
      <I size={15} className="opacity-80" />
      <span className="truncate">{item.label}</span>
    </NavLink>
  );
}

function SidebarContent({ role, onNavigate, user, onLogout }) {
  const groups = useNavGroups(role);
  return (
    <div className="flex flex-col h-full">
      <Link
        to="/"
        onClick={onNavigate}
        className="flex items-center gap-3 px-6 py-6 border-b border-white/5"
        data-testid="sidebar-brand"
      >
        <BrandLogo size={30} />
        <span className="font-display font-extrabold tracking-tight text-[16px]">Mirrorloops</span>
      </Link>

      <nav className="flex-1 overflow-y-auto py-4">
        {groups.map((g) => (
          <div key={g.key} className={g.title ? "mt-6" : ""}>
            {g.title && (
              <div className="px-6 mb-2 flex items-center gap-2">
                {g.icon && (
                  <g.icon
                    size={10}
                    className="opacity-70"
                    style={{ color: g.accent }}
                  />
                )}
                <span
                  className="text-[10px] uppercase tracking-[0.22em] font-bold"
                  style={{ color: g.accent || "rgba(255,255,255,0.4)" }}
                >
                  {g.title}
                </span>
              </div>
            )}
            <div className="space-y-0.5">
              {g.items.map((it) => (
                <SidebarLink key={it.to} item={it} onNavigate={onNavigate} />
              ))}
            </div>
          </div>
        ))}
      </nav>

      <div className="m-4 mt-2 p-4 border border-white/10 bg-white/[0.02]">
        <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-1">
          {user?.role?.replace("_", " ")}
        </div>
        <div className="text-sm font-bold truncate" data-testid="sidebar-user-name">
          {user?.name}
        </div>
        <div className="text-[11px] text-white/40 truncate font-mono">{user?.email}</div>
        <Button
          variant="ghost"
          onClick={onLogout}
          data-testid="sidebar-logout-btn"
          className="mt-3 w-full justify-start text-white/50 hover:text-white hover:bg-white/[0.04] px-2 h-8"
        >
          <LogOut size={14} className="mr-2" /> Log out
        </Button>
      </div>
    </div>
  );
}

/* ---------- Topbar (mobile + desktop minimal) ---------- */
function Topbar({ onOpenMenu, user }) {
  return (
    <div
      className="sticky top-0 z-30 lg:hidden h-14 flex items-center justify-between px-4 border-b border-white/10 bg-black/80 backdrop-blur"
      data-testid="app-topbar-mobile"
    >
      <button
        onClick={onOpenMenu}
        aria-label="Open navigation"
        data-testid="app-mobile-menu-btn"
        className="p-2 text-white"
      >
        <Menu size={20} />
      </button>
      <div className="flex items-center gap-2">
        <BrandLogo size={24} />
        <span className="font-display font-extrabold text-sm">Mirrorloops</span>
      </div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-bold">
        {user?.role?.replace("_", " ")}
      </div>
    </div>
  );
}

/* ---------- Root ---------- */
export default function AppShell() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Close drawer on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [loc.pathname, loc.search]);

  const doLogout = async () => {
    await logout();
    nav("/");
  };

  return (
    <div className="min-h-screen flex bg-[#0A0908] text-white font-sans">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-72 shrink-0 h-screen sticky top-0 border-r border-white/5 flex-col">
        <SidebarContent role={user?.role} user={user} onLogout={doLogout} />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm lg:hidden"
            onClick={() => setMobileOpen(false)}
            aria-hidden
          />
          <aside
            className="fixed inset-y-0 left-0 z-50 w-80 max-w-[88vw] bg-[#0A0908] border-r border-white/10 lg:hidden flex flex-col"
            data-testid="app-mobile-drawer"
          >
            <div className="absolute top-4 right-4">
              <button
                onClick={() => setMobileOpen(false)}
                aria-label="Close navigation"
                data-testid="app-mobile-drawer-close"
                className="p-2 text-white/70"
              >
                <X size={20} />
              </button>
            </div>
            <SidebarContent
              role={user?.role}
              user={user}
              onLogout={doLogout}
              onNavigate={() => setMobileOpen(false)}
            />
          </aside>
        </>
      )}

      <main className="flex-1 min-w-0 flex flex-col">
        <Topbar onOpenMenu={() => setMobileOpen(true)} user={user} />
        <div className="max-w-[1400px] w-full mx-auto px-6 md:px-10 py-8 flex-1">
          <Outlet />
        </div>
        <footer className="border-t border-white/5 py-6 text-center text-[11px] text-white/30" data-testid="app-footer-copy">
          © Mirrorloops Opc Pvt. Ltd.
        </footer>
      </main>
    </div>
  );
}
